import React, { useEffect, useMemo, useState } from "react";
import { HelpCircle, Search, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  moduleForRoute,
  publishedDictionary,
  searchDictionary,
  termsForRoute,
  viewForModule,
  termsForView,
} from "../help/rigoDictionary";
import { useHelpContext } from "../help/HelpContext";

function TermCard({ item, onNavigate }) {
  return (
    <article className="border rounded-2xl p-4 hover:bg-slate-50 transition">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h4 className="font-semibold text-slate-900">{item.term}</h4>
        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
          {item.category}
        </span>
      </div>
      <p className="mt-1 text-xs font-medium text-slate-500">Authority: {item.authority}</p>
      <p className="text-sm text-gray-600 mt-2 leading-6">{item.shortDefinition}</p>

      {item.executiveInterpretation && (
        <div className="mt-3 rounded-xl bg-indigo-50 border border-indigo-100 p-3">
          <p className="text-xs font-semibold text-indigo-700 mb-1">Executive interpretation</p>
          <p className="text-sm text-indigo-900 leading-6">{item.executiveInterpretation}</p>
        </div>
      )}

      {(item.technicalMeaning || item.caveats.length > 0) && (
        <details className="mt-3 text-sm text-slate-700">
          <summary className="cursor-pointer font-medium text-slate-600">Technical meaning and boundaries</summary>
          {item.technicalMeaning && <p className="mt-2 leading-6">{item.technicalMeaning}</p>}
          {item.caveats.length > 0 && (
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {item.caveats.map((caveat) => <li key={caveat}>{caveat}</li>)}
            </ul>
          )}
        </details>
      )}

      {item.moduleRoute && (
        <button onClick={() => onNavigate(item.moduleRoute)} className="mt-3 text-sm font-medium text-indigo-600 hover:text-indigo-800">
          Open module →
        </button>
      )}
    </article>
  );
}

export default function HelpCenter() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { currentViewId } = useHelpContext();

  const contextualModule = useMemo(() => moduleForRoute(location.pathname), [location.pathname]);
  const contextualView = useMemo(() => viewForModule(contextualModule, currentViewId), [contextualModule, currentViewId]);
  const contextualTerms = useMemo(() => contextualView ? termsForView(contextualView) : termsForRoute(location.pathname), [contextualView, location.pathname]);
  const contextualIds = useMemo(() => new Set(contextualTerms.map(({ id }) => id)), [contextualTerms]);
  const globalTerms = useMemo(() => publishedDictionary.filter(({ id }) => !contextualIds.has(id)), [contextualIds]);
  const results = useMemo(() => searchDictionary(query), [query]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  const close = () => { setOpen(false); setQuery(""); };
  const openModule = (path) => { close(); navigate(path); };

  return (
    <>
      <button onClick={() => setOpen(true)} aria-label="Open Help and Governance Guidance" className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-slate-950 text-white shadow-xl flex items-center justify-center hover:bg-slate-800 transition">
        <HelpCircle size={24} />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-end md:items-center justify-center p-4" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
          <section role="dialog" aria-modal="true" aria-labelledby="rigo-help-title" className="w-full max-w-3xl bg-white rounded-3xl shadow-2xl border overflow-hidden">
            <header className="flex items-center justify-between gap-4 p-5 border-b">
              <div>
                <h2 id="rigo-help-title" className="text-xl font-semibold">Help &amp; Governance Guidance</h2>
                <p className="text-sm text-gray-500">Understand RIGO concepts without leaving your current decision.</p>
              </div>
              <button onClick={close} aria-label="Close Help and Governance Guidance" className="h-9 w-9 shrink-0 rounded-full hover:bg-gray-100 flex items-center justify-center"><X size={20} /></button>
            </header>

            <div className="p-5">
              <label className="flex items-center gap-3 border rounded-2xl px-4 py-3 mb-5" htmlFor="rigo-help-search">
                <Search size={18} className="text-gray-400" />
                <span className="sr-only">Search the RIGO dictionary</span>
                <input id="rigo-help-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search EAL, P95, capital commitment, appetite…" className="w-full outline-none text-sm" autoFocus />
              </label>

              <div className="space-y-6 max-h-[min(65vh,560px)] overflow-y-auto pr-1">
                {query.trim() ? (
                  <section aria-labelledby="dictionary-results-heading">
                    <h3 id="dictionary-results-heading" className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">RIGO dictionary · {results.length} result{results.length === 1 ? "" : "s"}</h3>
                    <div className="space-y-3">{results.map((item) => <TermCard key={item.id} item={item} onNavigate={openModule} />)}</div>
                    {results.length === 0 && <p className="text-sm text-gray-400 text-center py-8">No guidance found for this search.</p>}
                  </section>
                ) : (
                  <>
                    {contextualModule && (
                      <section aria-labelledby="current-module-heading" className="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Current module</p>
                        <h3 id="current-module-heading" className="mt-1 text-lg font-semibold text-slate-950">{contextualModule.name}</h3>
                        <p className="mt-1 text-sm leading-6 text-slate-700">{contextualModule.orientation}</p>
                      </section>
                    )}
                    {contextualView && (
                      <section aria-labelledby="current-view-heading" className="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Current view</p>
                        <h3 id="current-view-heading" className="mt-1 text-lg font-semibold text-slate-950">{contextualView.name}</h3>
                        <p className="mt-1 text-sm leading-6 text-slate-700">{contextualView.orientation}</p>
                      </section>
                    )}
                    <section aria-labelledby="contextual-help-heading">
                      <h3 id="contextual-help-heading" className="mb-3 text-xs font-semibold uppercase tracking-wide text-indigo-700">{contextualView ? "Concepts on this view" : contextualModule ? "Concepts on this module" : "On this screen"} · {contextualTerms.length} concept{contextualTerms.length === 1 ? "" : "s"}</h3>
                      {contextualTerms.length > 0 ? <div className="space-y-3">{contextualTerms.map((item) => <TermCard key={item.id} item={item} onNavigate={openModule} />)}</div> : <p className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-600">No screen-specific terms are published yet. Search the full RIGO dictionary below.</p>}
                    </section>
                    <section aria-labelledby="all-terms-heading">
                      <h3 id="all-terms-heading" className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">All RIGO terms</h3>
                      <div className="space-y-3">{globalTerms.map((item) => <TermCard key={item.id} item={item} onNavigate={openModule} />)}</div>
                    </section>
                  </>
                )}
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
